import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { creditService, projectService, activityService } from '../../utils/supabaseQueries';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ManualEntryForm from './components/ManualEntryForm';
import ExcelUploadForm from './components/ExcelUploadForm';
import CreditPreview from './components/CreditPreview';
import ProjectActions from './components/ProjectActions';

const CreditCreation = () => {
  const { user, userProfile, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('manual');
  const [credits, setCredits] = useState([]);
  const [previewStyle, setPreviewStyle] = useState('title_card');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);

  const tabs = [
    { id: 'manual', label: 'Manual Entry', icon: 'Edit3' },
    { id: 'excel', label: 'Excel Upload', icon: 'FileSpreadsheet' }
  ];

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/user-dashboard', icon: 'LayoutDashboard' },
    { label: 'Create Credits', path: '/credit-creation', icon: 'Plus', isActive: true }
  ];

  // Load user's projects on component mount
  useEffect(() => {
    if (user && !authLoading) {
      loadUserProjects();
    }
  }, [user, authLoading]);

  // Load project credits when project is selected
  useEffect(() => {
    if (selectedProject) {
      loadProjectCredits(selectedProject?.id);
      setCurrentProjectId(selectedProject?.id);
    }
  }, [selectedProject]);

  const loadUserProjects = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: userProjects, error: projectsError } = await projectService?.getUserProjects();
      if (projectsError) {
        setError('Failed to load projects: ' + projectsError?.message);
        return;
      }
      setProjects(userProjects || []);
      
      // Auto-select first project if available
      if (userProjects?.length > 0 && !selectedProject) {
        setSelectedProject(userProjects?.[0]);
      }
    } catch (err) {
      setError('Failed to load projects. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const loadProjectCredits = async (projectId) => {
    if (!projectId) return;
    
    setLoading(true);
    setError(null);
    try {
      const { data: projectCredits, error: creditsError } = await creditService?.getProjectCredits(projectId);
      if (creditsError) {
        setError('Failed to load credits: ' + creditsError?.message);
        return;
      }
      
      // Transform database credits to component format
      const transformedCredits = (projectCredits || [])?.map(credit => ({
        id: credit?.id,
        role: credit?.role,
        name: credit?.person_name || '',
        character: credit?.character_name || (credit?.role === 'actor' ? '' : null),
        order: credit?.order_index || 0
      }));
      
      setCredits(transformedCredits);
    } catch (err) {
      setError('Failed to load credits. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!selectedProject) {
      setError('Please select a project first');
      return;
    }

    setIsProcessing(true);
    setUploadProgress(0);
    setError(null);

    try {
      // Simulate file processing with progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + Math.random() * 20;
        });
      }, 300);

      // Parse Excel file (mock parsing for now)
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const mockExcelCredits = [
        {
          personName: 'Christopher Nolan',
          role: 'director',
          characterName: null,
          orderIndex: 0
        },
        {
          personName: 'Emma Thomas',
          role: 'producer', 
          characterName: null,
          orderIndex: 1
        },
        {
          personName: 'Christian Bale',
          role: 'actor',
          characterName: 'Bruce Wayne',
          orderIndex: 2
        },
        {
          personName: 'Heath Ledger',
          role: 'actor',
          characterName: 'The Joker',
          orderIndex: 3
        },
        {
          personName: 'Wally Pfister',
          role: 'cinematographer',
          characterName: null,
          orderIndex: 4
        }
      ];

      // Save to database
      const { data: savedCredits, error: saveError } = await creditService?.createCreditsFromExcel(
        selectedProject?.id,
        mockExcelCredits
      );

      if (saveError) {
        throw new Error(saveError?.message);
      }

      clearInterval(progressInterval);
      setUploadProgress(100);

      // Log activity
      await activityService?.logActivity(
        selectedProject?.id,
        'credits_uploaded',
        { fileName: file?.name, creditsCount: mockExcelCredits?.length }
      );

      // Reload credits
      await loadProjectCredits(selectedProject?.id);

    } catch (err) {
      setError('Failed to process Excel file: ' + err?.message);
    } finally {
      setIsProcessing(false);
      setUploadProgress(0);
    }
  };

  const handleSaveProject = async (projectData) => {
    setIsSaving(true);
    setError(null);

    try {
      // Create new project if needed
      if (!selectedProject) {
        const { data: newProject, error: projectError } = await projectService?.createProject({
          name: projectData?.name,
          description: 'Film credits project',
          status: 'draft'
        });

        if (projectError) {
          throw new Error(projectError?.message);
        }

        setSelectedProject(newProject);
        setCurrentProjectId(newProject?.id);
        
        // Log activity
        await activityService?.logActivity(
          newProject?.id,
          'project_created',
          { projectName: projectData?.name }
        );

        // Reload projects list
        await loadUserProjects();
      }

      // Update project metadata if needed
      if (selectedProject && (projectData?.style || projectData?.name !== selectedProject?.name)) {
        const updates = {};
        if (projectData?.name && projectData?.name !== selectedProject?.name) {
          updates.name = projectData?.name;
        }
        if (projectData?.style) {
          updates.metadata = { 
            ...selectedProject?.metadata, 
            previewStyle: projectData?.style 
          };
        }

        if (Object.keys(updates)?.length > 0) {
          await projectService?.updateProject(selectedProject?.id, updates);
        }
      }

    } catch (err) {
      setError('Failed to save project: ' + err?.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreditsChange = async (newCredits) => {
    if (!selectedProject) {
      setError('Please select a project first');
      return;
    }

    // Add this block - Define creditCreationService with validation function
    const creditCreationService = {
      validateCredits: (credits) => {
        const errors = [];
        
        if (!Array.isArray(credits)) {
          errors.push('Credits must be an array');
          return { isValid: false, errors };
        }
        
        credits.forEach((credit, index) => {
          if (!credit.name || !credit.name.trim()) {
            errors.push(`Credit ${index + 1}: Name is required`);
          }
          if (!credit.role) {
            errors.push(`Credit ${index + 1}: Role is required`);
          }
        });
        
        return {
          isValid: errors.length === 0,
          errors
        };
      }
    };

    // Validate credits before processing
    const validation = creditCreationService?.validateCredits(newCredits);
    if (!validation?.isValid) {
      setError(`Invalid credits: ${validation?.errors?.join(', ')}`);
      return;
    }

    setCredits(newCredits);

    // Auto-save credits to database with better error handling
    try {
      // Clear existing credits for this project
      const { data: existingCredits } = await creditService?.getProjectCredits(selectedProject?.id);
      if (existingCredits?.length > 0) {
        for (const credit of existingCredits) {
          await creditService?.deleteCredit(credit?.id);
        }
      }

      // Insert new credits with proper role mapping
      for (const credit of newCredits) {
        if (credit?.name?.trim()) {
          const creditData = {
            projectId: selectedProject?.id,
            personName: credit?.name,
            role: credit?.role, // Use exact role value from form
            characterName: credit?.character,
            orderIndex: credit?.order
          };

          const { error: createError } = await creditService?.createCredit(creditData);
          if (createError) {
            console.error('Error creating credit:', createError);
            setError(`Failed to save credit for ${credit?.name}: ${createError?.message}`);
            return; // Stop processing on first error
          }
        }
      }

      // Log activity
      await activityService?.logActivity(
        selectedProject?.id,
        'credits_updated',
        { creditsCount: newCredits?.length }
      );

    } catch (err) {
      console.error('Credits save error:', err);
      setError('Failed to save credits: ' + err?.message);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header
          userRole={userProfile?.role || user?.user_metadata?.role || 'user'}
          userName={userProfile?.full_name || user?.user_metadata?.full_name || user?.email}
          userAvatar={userProfile?.avatar_url}
        />
        <main className="pt-16">
          <div className="container mx-auto px-4 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Icon name="Loader2" size={32} className="animate-spin text-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Loading credits...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header
        userRole={userProfile?.role || user?.user_metadata?.role || 'user'}
        userName={userProfile?.full_name || user?.user_metadata?.full_name || user?.email}
        userAvatar={userProfile?.avatar_url}
      />

      <main className="pt-16">
        <div className="container mx-auto px-4 py-8">
          <Breadcrumb customItems={breadcrumbItems} />

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-center gap-3">
                <Icon name="AlertCircle" size={20} className="text-error" />
                <div>
                  <p className="text-error font-medium">{error}</p>
                  <button
                    onClick={() => setError(null)}
                    className="text-sm text-error/70 hover:text-error underline mt-1"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Project Selection */}
          {projects?.length > 0 && (
            <div className="mb-6 bg-card border border-border rounded-lg p-4">
              <label className="block text-sm font-medium text-foreground mb-2">
                Select Project
              </label>
              <select
                value={selectedProject?.id || ''}
                onChange={(e) => {
                  const project = projects?.find(p => p?.id === e?.target?.value);
                  setSelectedProject(project);
                }}
                className="w-full max-w-md px-3 py-2 border border-border rounded-md bg-background text-foreground"
              >
                <option value="">Create New Project</option>
                {projects?.map(project => (
                  <option key={project?.id} value={project?.id}>
                    {project?.name} ({project?.status})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Page Header */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  {selectedProject ? `Edit Credits: ${selectedProject?.name}` : 'Create Film Credits'}
                </h1>
                <p className="text-muted-foreground">
                  Build professional film credit sequences with manual entry or Excel upload
                </p>
                {selectedProject && (
                  <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                    <span>Project: {selectedProject?.name}</span>
                    <span>Status: {selectedProject?.status}</span>
                    <span>Credits: {credits?.length}</span>
                  </div>
                )}
              </div>
              
              {/* Mobile Preview Toggle */}
              <div className="lg:hidden">
                <Button
                  variant="outline"
                  iconName={showPreview ? "EyeOff" : "Eye"}
                  iconPosition="left"
                  onClick={() => setShowPreview(!showPreview)}>

                  {showPreview ? 'Hide' : 'Show'} Preview
                </Button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Input Section */}
            <div className="space-y-6">
              {/* Tab Navigation */}
              <div className="bg-card border border-border rounded-lg p-1">
                <div className="flex">
                  {tabs?.map((tab) =>
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-4 py-3 rounded-md text-sm font-medium transition-micro flex-1 justify-center ${
                    activeTab === tab?.id ?
                    'bg-primary text-primary-foreground shadow-sm' :
                    'text-muted-foreground hover:text-foreground hover:bg-muted'}`
                    }>

                      <Icon name={tab?.icon} size={16} />
                      <span className="hidden sm:inline">{tab?.label}</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Tab Content */}
              <div className="min-h-[400px]">
                {activeTab === 'manual' &&
                <ManualEntryForm
                  credits={credits}
                  onCreditsChange={handleCreditsChange} />

                }

                {activeTab === 'excel' &&
                <ExcelUploadForm
                  onFileUpload={handleFileUpload}
                  uploadProgress={uploadProgress}
                  isProcessing={isProcessing} />

                }
              </div>

              {/* Project Actions - Mobile */}
              <div className="lg:hidden">
                <ProjectActions
                  credits={credits}
                  previewStyle={previewStyle}
                  onSave={handleSaveProject}
                  isSaving={isSaving}
                  selectedProject={selectedProject} />

              </div>
            </div>

            {/* Preview Section */}
            <div className={`space-y-6 ${showPreview ? 'block' : 'hidden lg:block'}`}>
              <CreditPreview
                credits={credits}
                previewStyle={previewStyle}
                onStyleChange={setPreviewStyle} />


              {/* Project Actions - Desktop */}
              <div className="hidden lg:block">
                <ProjectActions
                  credits={credits}
                  previewStyle={previewStyle}
                  onSave={handleSaveProject}
                  isSaving={isSaving}
                  selectedProject={selectedProject} />

              </div>
            </div>
          </div>

          {/* Help Section */}
          <div className="mt-12 bg-card border border-border rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="HelpCircle" size={24} className="text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Need Help?</h3>
                <p className="text-muted-foreground mb-4">
                  Learn how to create professional film credits with our comprehensive guides and tutorials.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" size="sm" iconName="FileText" iconPosition="left">
                    User Guide
                  </Button>
                  <Button variant="outline" size="sm" iconName="Video" iconPosition="left">
                    Video Tutorial
                  </Button>
                  <Button variant="outline" size="sm" iconName="MessageCircle" iconPosition="left">
                    Contact Support
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CreditCreation;